import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { L10N_LOCALE, L10nLocale, L10nTranslationService } from 'angular-l10n';
import { PagedRolesResponse, Role, SettingsApi } from 'src/app/hickory-api';
import { RolesDataSource } from '../data-table/datasource/roles-datasource';
import { UntypedFormControl, FormControl } from '@angular/forms';
import { SelectionModel } from '@angular/cdk/collections';

import { catchError, debounceTime, finalize, Observable, of, Subject, takeUntil, tap } from 'rxjs';
import { MatPaginator, PageEvent } from '@angular/material/paginator';

export interface SelectRolesDialogData {
  selectedRoles: number[];
  tenantId: string;
}

export class RoleSelection {
  selected: boolean;
  id: number;
  name: string;
  
  constructor(selected: boolean = false) {
    this.selected = selected;
  }
}

@Component({
  selector: 'app-roles-select-component',
  templateUrl: './roles-select-component.component.html',
  styleUrls: ['./roles-select-component.component.scss'],
  standalone: false
})

export class RolesSelectComponentComponent implements OnInit, OnDestroy {
  @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;

  availableRoles: Role[] = [];
  totalRoleCount: number = 0;
  defaultPageSize: number = 200;

  page = 1;
  rolesPerPage = 200;
  dataSource: RolesDataSource;
  showSystemRoles: boolean;
  roleSearchControl = new UntypedFormControl();
  searchControl = new FormControl();
  displayedColumns: string[] = ['name'];
  isLoading: boolean = false;
  selected: SelectionModel<number> = new SelectionModel<number>(true, []);
  idToRole: Map<number, Role> = new Map<number, Role>();

  destroy$: Subject<void> = new Subject<void>();

  get selectedRoleIds(): number[] { return this.selected.selected; }
  get selectedRoles(): Role[] { return this.selected.selected.map((c) => this.idToRole.get(c)); }
  get areSomeSelected(): boolean { return !!this.selected.selected.length; }
  get areAllSelected(): boolean { return !!this.availableRoles?.length && this.availableRoles?.every((c) => this.isSelected(c)); }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: SelectRolesDialogData,
    @Inject(L10N_LOCALE) public locale: L10nLocale,
      private readonly translation: L10nTranslationService,
      private readonly settingsService: SettingsApi
  ) { }

  ngOnInit() {
    this.getPreselectedRoles(this.data.selectedRoles);
    this.getRoles(1, this.defaultPageSize, '');

    this.searchControl.valueChanges
      .pipe(
        debounceTime(500),
        tap((searchValue: string) => {
          this.getRoles(1, this.paginator?.pageSize, searchValue);
        }),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  isSelected(role: Role): boolean { return this.selected.isSelected(role.id); }
  onSelectionChange(role: Role): void { this.selected.toggle(role.id); }

  get noRolesText(): string {
    return this.translation.translate('Role.NoRoles', null, this.locale.language);
  }

  get addRolesText(): string {
    return this.translation.translate('Role.Add', null, this.locale.language);
  }

  onToggleAll(): void {
    if (!this.areAllSelected) {
      this.availableRoles.forEach((contact) => this.selected.select(contact.id));
      return;
    }
    
    this.selected.clear();
  }

  getPreselectedRoles(roleIds: number[]) {
    if (!roleIds?.length) {
      return;
    }

    roleIds.forEach((roleId) => {
      this.selected.select(roleId);
    })
  }

  getRoles(page: number = 1,
      pageSize: number = this.defaultPageSize,
      searchTerm: string = ''
    ): void {
      this.isLoading = true;

      const request$: Observable<PagedRolesResponse>  = this.settingsService.getRolesV4(Number.parseInt(this.data.tenantId), page, pageSize, null, true);
      request$
        .pipe(
          catchError((error) => {
        this.isLoading = false;
        return of(error);
      }),
      tap((roles: PagedRolesResponse)=> {
        this.availableRoles = roles.items ?? [];
        this.availableRoles.forEach((role) => this.idToRole.set(role.id, role));
        if (this.paginator != null) {
          this.paginator.pageIndex = roles?.page  - 1;
        }

        // The API doesn't have an option to search for a given role
        if (searchTerm && searchTerm.length > 0) {
          this.availableRoles = this.availableRoles.filter(role => role?.name.toLowerCase().includes(searchTerm.toLocaleLowerCase()));
          this.totalRoleCount = roles.totalItemCount;
        }
        else {
          this.totalRoleCount = roles.totalItemCount;
        }
      }),
      takeUntil(this.destroy$),
      finalize(() =>(this.isLoading = false)),
    )
    .subscribe();
  }

  onPageChange(event: PageEvent): void {
    this.getRoles(event.pageIndex + 1, event.pageSize, this.searchControl.value);
  }
}
